/* ==========================================================================
   SmartHome-Energy — Homepage core interactions (Vanilla JS, no libraries)
   - Scroll reveals via IntersectionObserver
   - Animated counters
   - Accessible accordions (FAQ)
   - Energy calculator
   - Before/after inspiration slider (drag on the handle only)
   Guarded, idempotent, and safe to load with `defer`.
   ========================================================================== */
(function () {
  "use strict";
  document.documentElement.classList.add("js");
  if (window.__sheHomeInit) return;      /* run once even if included by many sections */
  window.__sheHomeInit = true;

  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- Scroll reveal ---------- */
  function initReveals() {
    var els = document.querySelectorAll(".she-reveal, .she-stagger");
    if (!els.length) return;
    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-inview"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-inview");
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Animated counters ---------- */
  function animateCount(el) {
    if (el.__sheCounted) return;
    el.__sheCounted = true;
    var target = parseFloat(el.getAttribute("data-she-count"));
    if (isNaN(target)) return;
    var decimals = parseInt(el.getAttribute("data-she-decimals") || "0", 10);
    var suffix = el.getAttribute("data-she-suffix") || "";
    var prefix = el.getAttribute("data-she-prefix") || "";
    var dur = reduce ? 0 : 1400;
    var start = null;
    function frame(ts) {
      if (start === null) start = ts;
      var p = dur === 0 ? 1 : Math.min((ts - start) / dur, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      var val = (target * eased).toFixed(decimals);
      el.textContent = prefix + Number(val).toLocaleString("de-DE") + suffix;
      if (p < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }
  function initCounters() {
    var els = document.querySelectorAll("[data-she-count]");
    if (!els.length) return;
    if (!("IntersectionObserver" in window)) { els.forEach(animateCount); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { animateCount(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Accessible accordion (FAQ) ---------- */
  function initAccordions() {
    var groups = document.querySelectorAll("[data-she-accordion]");
    groups.forEach(function (group) {
      if (group.__sheAccBound) return;
      group.__sheAccBound = true;
      var single = group.getAttribute("data-she-accordion") === "single";
      var triggers = group.querySelectorAll(".she-acc__trigger");
      triggers.forEach(function (btn) {
        btn.addEventListener("click", function () {
          var item = btn.closest(".she-acc__item");
          var panel = item.querySelector(".she-acc__panel");
          var open = btn.getAttribute("aria-expanded") === "true";
          if (single && !open) {
            group.querySelectorAll(".she-acc__trigger[aria-expanded='true']").forEach(function (o) {
              o.setAttribute("aria-expanded", "false");
              var op = o.closest(".she-acc__item").querySelector(".she-acc__panel");
              op.style.maxHeight = null;
            });
          }
          btn.setAttribute("aria-expanded", String(!open));
          panel.style.maxHeight = open ? null : panel.scrollHeight + "px";
        });
      });
    });
  }

  /* ======================================================================
     Energy calculator
     ----------------------------------------------------------------------
     Model (annual balance, deliberately transparent — not a simulation):

     1) Demand   Household base per person (AT averages) + optional EV +
                 optional heat pump.
     2) Yield    Specific annual yield per kWp depends on roof orientation.
                 Values come from the section settings (default AT, 30-35 deg):
                 south 1050, east/west 890, north 660 kWh/kWp per year.
     3) Sizing   Array size is scaled to demand and *partially* compensates a
                 weaker orientation (exponent 0.4) — a north roof gets a
                 somewhat larger array, but not one that fully offsets the
                 yield loss, because roof area and payback set a practical
                 limit. Battery approx. 1.2 kWh per 1000 kWh demand, capped
                 by array size.
     4) Autarky  Smooth fit calibrated against the HTW Berlin / Quaschning
                 reference points for German/Austrian households:
                   x = annual yield / annual demand
                   y = usable battery kWh per 1000 kWh demand
                   x=1, y=0  ->  approx. 30 %     x=1, y=1  ->  approx. 55 %
                   x=2, y=2  ->  approx. 70 %     ceiling   ->  92 % (seasonal)
                 A heat pump shifts demand into winter, so its share reduces
                 the achievable self-sufficiency (EV to a much smaller degree).
     5) Money    savings = self-used kWh * electricity price
                         + exported kWh * feed-in tariff
                 Battery round-trip losses (88 %) are taken off the PV side.
     ====================================================================== */

  /* Household electricity demand in kWh/a by number of persons (index 1-8) */
  var SHE_HOUSEHOLD_KWH = [0, 2100, 3100, 3900, 4600, 5200, 5800, 6300, 6800];

  function sheRound05(v) { return Math.round(v * 2) / 2; }
  function sheClamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function sheNum(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return (isNaN(v) || v < 0) ? fallback : v;
  }
  function sheFmt(n, decimals) {
    return Number(n.toFixed(decimals || 0)).toLocaleString("de-DE", {
      minimumFractionDigits: decimals || 0,
      maximumFractionDigits: decimals || 0
    });
  }

  function initCalculators() {
    var calcs = document.querySelectorAll("[data-she-calculator]");
    calcs.forEach(function (calc) {
      if (calc.__sheCalcBound) return;
      calc.__sheCalcBound = true;

      var out = {};
      calc.querySelectorAll("[data-out]").forEach(function (el) {
        out[el.getAttribute("data-out")] = el;
      });

      var persons     = calc.querySelector("[name='persons']");
      var personsOut  = calc.querySelector("[data-persons-out]");
      var roofInputs  = calc.querySelectorAll("[name='roof']");
      var ev          = calc.querySelector("[name='ev']");
      var heat        = calc.querySelector("[name='heatpump']");

      var priceKwh    = sheNum(calc, "data-price-kwh", 0.30) || 0.30;
      var priceFeedIn = sheNum(calc, "data-price-feedin", 0.06);
      var evKwh       = sheNum(calc, "data-ev-kwh", 2500);
      var hpKwh       = sheNum(calc, "data-hp-kwh", 4200);
      var refYield    = sheNum(calc, "data-ref-yield", 1050) || 1050;

      /* Specific yield of the SELECTED orientation — the previous version
         always read the first radio, which is why nothing ever changed. */
      function specificYield() {
        for (var i = 0; i < roofInputs.length; i++) {
          if (roofInputs[i].checked) {
            var v = parseFloat(roofInputs[i].value);
            if (!isNaN(v) && v > 0) return v;
          }
        }
        return refYield;
      }

      function calcFn() {
        /* --- 1) Demand ------------------------------------------------ */
        var p = sheClamp(parseInt((persons && persons.value) || "3", 10) || 3, 1, 8);
        var eHouse = SHE_HOUSEHOLD_KWH[p];
        var eEv    = (ev && ev.checked) ? evKwh : 0;
        var eHp    = (heat && heat.checked) ? hpKwh : 0;
        var demand = eHouse + eEv + eHp;

        /* --- 2) + 3) Yield and system size ---------------------------- */
        var spec        = specificYield();
        var targetRatio = eHp > 0 ? 1.25 : 1.10;          /* generation / demand */
        var compensate  = Math.pow(refYield / spec, 0.4); /* partial orientation offset */
        var kwp         = sheClamp(sheRound05(demand * targetRatio / refYield * compensate), 3, 20);
        var yieldKwh    = kwp * spec;

        var storage = sheClamp(sheRound05(demand / 1000 * 1.2), 4, 20);
        storage     = Math.min(storage, sheRound05(kwp * 1.6));

        /* --- 4) Self-sufficiency -------------------------------------- */
        var x = yieldKwh / demand;
        var y = storage / (demand / 1000);
        var season = 1 - 0.40 * (eHp / demand) - 0.10 * (eEv / demand);

        var aDirect = 0.45 * x / (x + 0.50) * season;   /* without battery       */
        var aMax    = 0.95 * x / (x + 0.40) * season;   /* large-battery ceiling */
        var autarky = aDirect + (aMax - aDirect) * (y / (y + 0.52));
        autarky = sheClamp(autarky, 0, 0.92);

        /* --- 5) Energy and money -------------------------------------- */
        var selfUsed = autarky * demand;                     /* kWh from own roof */
        var direct   = Math.min(aDirect * demand, selfUsed);
        var pvToHome = direct + (selfUsed - direct) / 0.88;  /* battery losses    */
        if (pvToHome > yieldKwh && pvToHome > 0) {           /* physical guard    */
          var f = yieldKwh / pvToHome;
          pvToHome = yieldKwh;
          selfUsed *= f;
          autarky  *= f;
        }
        var feedIn  = Math.max(0, yieldKwh - pvToHome);
        var savings = selfUsed * priceKwh + feedIn * priceFeedIn;

        var sys = kwp < 6 ? "Starter" : kwp < 12 ? "Family" : kwp < 20 ? "Premium" : "Business";

        /* --- Output ---------------------------------------------------- */
        if (personsOut)   personsOut.textContent   = String(p);
        if (out.demand)   out.demand.textContent   = sheFmt(Math.round(demand / 50) * 50);
        if (out.power)    out.power.textContent    = sheFmt(kwp, 1);
        if (out.storage)  out.storage.textContent  = sheFmt(storage, 1);
        if (out.autarky)  out.autarky.textContent  = sheFmt(autarky * 100);
        if (out.yield)    out.yield.textContent    = sheFmt(Math.round(yieldKwh / 50) * 50);
        if (out.savings)  out.savings.textContent  = sheFmt(Math.round(savings));
        if (out.system)   out.system.textContent   = sys;
      }

      calc.addEventListener("input", calcFn);
      calc.addEventListener("change", calcFn);
      calcFn();
    });
  }

  /* ---------- Before / After inspiration slider ----------
     Dragging is bound to the handle only. Binding it to the whole tile made
     a horizontal swipe inside the carousel move the divider instead of
     turning the page — on touch devices the carousel was unusable. The
     range input stays in the markup for keyboard operation. */
  var sheBaActive = null;
  function sheBaFromEvent(e) {
    if (!sheBaActive) return;
    var rect = sheBaActive.getBoundingClientRect();
    var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    sheBaActive.__sheBaSet((x / rect.width) * 100);
  }
  function initBeforeAfter() {
    var els = document.querySelectorAll("[data-she-ba]");
    if (!els.length) return;
    els.forEach(function (ba) {
      if (ba.__sheBaBound) return;
      ba.__sheBaBound = true;
      var range = ba.querySelector(".she-ba__range");
      var handle = ba.querySelector(".she-ba__handle");
      function set(v) {
        v = Math.max(0, Math.min(100, v));
        ba.style.setProperty("--she-ba-pos", v + "%");
        if (range) range.value = v;
      }
      ba.__sheBaSet = set;
      if (range) range.addEventListener("input", function () { set(parseFloat(range.value)); });

      var grip = handle || range;
      if (grip) {
        grip.addEventListener("pointerdown", function (e) {
          sheBaActive = ba;
          e.preventDefault();
          sheBaFromEvent(e);
        });
      }
      set(50);
    });
    if (!window.__sheBaGlobal) {
      window.__sheBaGlobal = true;
      window.addEventListener("pointermove", function (e) { if (sheBaActive) sheBaFromEvent(e); });
      window.addEventListener("pointerup", function () { sheBaActive = null; });
      window.addEventListener("pointercancel", function () { sheBaActive = null; });
    }
  }

  function boot() {
    initReveals();
    initCounters();
    initAccordions();
    initCalculators();
    initBeforeAfter();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Re-init on Shopify Theme Editor events */
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener("shopify:section:load", boot);
  }
})();
